<template>
    <gi-form
        v-if="form"
        ref="form"
        :form="form"
        :on-submit-fn="onSubmitFn"
        :submit-button-text="$t('create_proposal')"
        :hide-actions="hideActions"
        @success="onSuccess"
        @error="onError"
        @form-error="onFormError"
    >
        <template #prepend>
            <proposal-rebalance-modal
                v-if="form.offer"
                v-model="rebalancing"
                :proposal="form"
                prevent-patch
                @update:proposal="$event => (form = $event.proposal)"
            />
        </template>

        <template v-if="!hideHeader" #header>
            <app-page-header class="mb-4" />
        </template>

        <template #fields="{ loading }">
            <template v-if="!offerId">
                <p class="primaryText--text mb-2">
                    {{ $t('which_offer_is_this_proposal_for') }}
                </p>
                <field-offer v-model="selectedOfferId" :disabled="loading" required />
            </template>

            <template v-if="shortlisted && !shortlistId">
                <p class="primaryText--text mb-2">
                    {{ $t('select_a_shortlist_to_add_this_offer_to') }}
                </p>
                <field-shortlist
                    v-model="selectedShortlistId"
                    v-test-id="'offer-input-shortlist'"
                    :disabled="loading"
                    required
                    allow-create
                    @input:object="onShortlistInput"
                />
            </template>

            <template v-else>
                <template v-if="!adviserId && isGrowthInvest()">
                    <p class="primaryText--text mb-2">
                        {{ $t('which_adviser_is_this_proposal_for') }}
                    </p>
                    <field-adviser v-model="selectedAdviserId" :disabled="loading" required />
                </template>

                <template v-if="!clientId && isAdviser() && form && form.adviser_id">
                    <p class="primaryText--text mb-2">
                        {{ $t('which_client_is_this_proposal_for') }}
                    </p>
                    <field-investor
                        v-model="selectedClientId"
                        :disabled="loading"
                        required
                        :filters="investorFilters"
                    />
                </template>
            </template>

            <app-alert
                v-if="form.client && !form.client.has_address_history"
                dense
                color="primaryText"
                :icon="getIcon('error')"
                class="mb-7"
            >
                <template #text>
                    <p class="primaryText--text mb-2">
                        {{
                            $t('you_need_three_years_address_history') +
                            ' ' +
                            (isInvestor()
                                ? $t('please_update_your_address_history')
                                : $t('please_update_the_clients_address_history'))
                        }}
                    </p>
                    <app-button
                        text="Update address history"
                        icon="next"
                        class="mb-2 d-block"
                        @click="
                            go({
                                name: 'SettingsProfile',
                                query: { organisation_id: form.client_id, organisation_type: 'investor' }
                            })
                        "
                    />
                </template>
            </app-alert>

            <template v-else>
                <proposal-financials
                    ref="financials"
                    :proposal="form"
                    :readonly="isReadonly"
                    :shortlisted="shortlisted"
                    class="mx-n7"
                    @input:proposal="form = $event"
                    @editing="$emit('editing:financials', $event)"
                    @rebalance="rebalancing = $event"
                />

                <template v-if="!shortlisted && !shortlistId && !hasPreset">
                    <app-divider hidden class="mb-7" />

                    <p class="primaryText--text mb-2">
                        {{ $t('would_you_like_to_provide_any_additional_information') }}
                    </p>
                    <field-description v-model="notes" :disabled="loading" :label="$t('additional_information')" />
                </template>
            </template>
        </template>

        <template #prepend-actions>
            <app-button-cancel v-if="prependCancelButton" @click="onCancel" />
        </template>
    </gi-form>
</template>

<script>
import form from '@/mixins/form';
import { Proposal, Shortlist } from '@/models';
import ActivityTypeEnum from '@/enums/activity/type';
// import ProposalStatusEnum from '@/enums/proposal/status';
import OrganisationStatusEnum from '@/enums/organisation/status';

export default {
    name: 'CreateProposal',
    components: {
        GiForm: () =>
            import(
                /* webpackChunkName: "proposal" */
                '@/components/forms/Form'
            ),
        FieldOffer: () =>
            import(
                /* webpackChunkName: "proposal" */
                '@/components/fields/Offer'
            ),
        FieldAdviser: () =>
            import(
                /* webpackChunkName: "proposal" */
                '@/components/fields/Adviser'
            ),
        FieldInvestor: () =>
            import(
                /* webpackChunkName: "proposal" */
                '@/components/fields/Investor'
            ),
        FieldShortlist: () =>
            import(
                /* webpackChunkName: "proposal" */
                '@/components/fields/Shortlist'
            ),
        ProposalFinancials: () =>
            import(
                /* webpackChunkName: "proposal" */
                '@/views/ProposalFinancials'
            ),
        ProposalRebalanceModal: () =>
            import(
                /* webpackChunkName: "investments/proposals" */
                '@/views/ProposalRebalanceModal'
            )
    },
    mixins: [form],
    props: {
        shortlisted: {
            type: Boolean,
            default: false
        },
        shortlistId: {
            type: String,
            default: null
        },
        offerId: {
            type: String,
            default: null
        },
        adviserId: {
            type: String,
            default: null
        },
        clientId: {
            type: String,
            default: null
        },
        readonly: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        id: 'CreateProposal',
        model: Proposal,
        forceReadonly: false,
        selectedOfferId: null,
        selectedAdviserId: null,
        selectedClientId: null,
        selectedShortlistId: null,
        notes: '',
        updateCacheWithGetAfterSubmit: false,
        rebalancing: false
    }),
    computed: {
        investorFilters() {
            if (!this.form || !this.form.adviser_id) {
                return null;
            }

            return {
                adviser_id: `${this.getFilterOperator('is')}:${this.form.adviser_id}`,
                status: `${this.getFilterOperator('in')}:[${OrganisationStatusEnum.ACTIVE},${
                    OrganisationStatusEnum.UNVERIFIED
                }]`,
                investment_account_authorised_at: `${this.getFilterOperator('is_before')}:${this.now()
                    .add(1, 'day')
                    .format('YYYY-MM-DD 00:00:00')}`
            };
        },
        isReadonly() {
            return this.forceReadonly || this.readonly || !this.form || !this.form.adviser_id || !this.form.client_id;
        }
    },
    watch: {
        form: {
            deep: true,
            handler(form) {
                if (form) {
                    this.$emit('update:form', form);
                }
            }
        },
        shortlisted(shortlisted) {
            // if (this.form && this.form.status !== ProposalStatusEnum.SCHEDULED) {
            //     this.form.status = shortlisted ? ProposalStatusEnum.SHORTLISTED : null;
            // }

            if (shortlisted) {
                this.formRequest = Shortlist.api.putEntry;
            } else {
                this.formRequest = null;
            }
        },
        shortlistId(shortlistId) {
            this.selectedShortlistId = shortlistId;
        },
        selectedShortlistId(shortlistId) {
            if (this.form) {
                this.form.shortlist_id = shortlistId;
            }
        },
        'form.shortlist_id'(shortlistId) {
            if (this.form) {
                if (!shortlistId) {
                    this.form.shortlist = null;
                }
            }
        },
        offerId(offerId) {
            this.selectedOfferId = offerId;
        },
        selectedOfferId(offerId) {
            if (this.form) {
                this.form.offer_id = offerId;
            }
        },
        async 'form.offer_id'(offerId, oldOfferId) {
            if (!this.form) {
                return;
            }

            if (offerId !== oldOfferId && oldOfferId) {
                await this.form.resetInvestments();
                await this.form.resetFees();
            }

            if (!offerId) {
                this.form.offer = null;
            }

            if (this.hasPreset) {
                return;
            }

            await this.form.recalculateInvestments();
            await this.form.recalculateFees();
        },
        adviserId(adviserId) {
            this.selectedAdviserId = adviserId;
        },
        selectedAdviserId(adviserId) {
            if (this.form) {
                this.form.adviser_id = adviserId;
                this.form.client_id = null;
            }
        },
        'form.adviser_id'(adviserId) {
            if (this.form && !this.hasPreset) {
                if (!adviserId) {
                    this.form.adviser = null;
                }
            }
        },
        clientId(clientId) {
            this.selectedClientId = clientId;
        },
        selectedClientId(clientId) {
            if (this.form) {
                this.form.client_id = clientId;
            }
        },
        async 'form.client_id'(clientId) {
            if (this.form && !this.hasPreset) {
                this.form.signatories = [];

                if (!clientId) {
                    this.form.client = null;
                }

                await this.form.setup();

                this.forceReadonly = true;
                await this.$nextTick();
                this.forceReadonly = false;
            }
        }
    },
    methods: {
        async beforeSubmit() {
            if (!this.shortlisted) {
                let text = [this.notes.trim()];
                this.form.activities = [
                    {
                        icon: 'NOTE',
                        date: this.now().format('YYYY-MM-DD'),
                        title: this.$t('proposal_created'),
                        text: text.join('\n') || this.$t('no_additional_information'),
                        scope: 'TASK',
                        type: ActivityTypeEnum.PROPOSAL
                    }
                ];
            }

            return Promise.resolve();
        },
        async afterReset() {
            this.$emit('editing:financials', false);

            this.selectedOfferId = null;
            this.selectedAdviserId = null;
            this.selectedClientId = null;
            this.selectedShortlistId = null;
            this.notes = '';

            await this.afterSetup();
        },
        async afterSetup() {
            if (this.shortlisted) {
                // if (this.form && this.form.status !== ProposalStatusEnum.SCHEDULED) {
                //     this.form.status = ProposalStatusEnum.SHORTLISTED;
                // }

                if (this.shortlisted) {
                    this.formRequest = Shortlist.api.putEntry;
                } else {
                    this.formRequest = null;
                }
            } else if (this.hasPreset) {
                this.formRequest = async (form, config = {}) => {
                    await Shortlist.api.putEntry(form);
                    return await Shortlist.api.proposeEntry(form, config);
                };
            }

            if (this.shortlistId) {
                this.selectedShortlistId = this.shortlistId;
                if (this.form) {
                    this.form.shortlist_id = this.shortlistId;
                }
            }

            if (this.offerId) {
                this.selectedOfferId = this.offerId;
                if (this.form) {
                    this.form.offer_id = this.offerId;
                }
            }

            if (this.adviserId) {
                this.selectedAdviserId = this.adviserId;
                if (this.form) {
                    this.form.adviser_id = this.adviserId;
                }
            }

            if (this.clientId) {
                this.selectedClientId = this.clientId;
                if (this.form) {
                    this.form.client_id = this.clientId;
                }
            }

            if (!this.hasPreset) {
                await this.form.resetOrganisations();
            }

            await this.form.setup();
        },
        onShortlistInput(shortlist) {
            if (!this.form) {
                return;
            }

            if (shortlist) {
                this.selectedAdviserId = shortlist.adviser_id;
                this.selectedClientId = shortlist.investor_id;
            } else {
                this.selectedAdviserId = null;
                this.selectedClientId = null;
            }
        }
    }
};
</script>
